import { WidgetModule, WidgetProps, Widgets } from '#scripts/widgets/types.js';
import { siteUrl } from '#utils/template.js';

export async function createWidgetComponent<T extends Widgets>(
  widget: T,
  widgetModule: WidgetModule,
  widgetData: WidgetProps<T>,
): Promise<HTMLElement> {
  const creatorMap: {
    [K in Widgets]: (module: WidgetModule, props: WidgetProps<K>) => HTMLElement;
  } = {
    [Widgets.Reviews]: (m, props) => m.createReviewsComponent(props),
    [Widgets.MicroCombo]: (m, props) => m.createMicroComboComponent(props),
    [Widgets.ReviewsMini]: (m, props) => m.createReviewsMiniComponent(props),
    [Widgets.ReviewsMicro]: (m, props) => m.createReviewsMicroComponent(props),
    [Widgets.MicroTrustscore]: (m, props) => m.createMicroTrustscoreComponent(props),
    [Widgets.Stars]: (m, props) => m.createStarsComponent(props),
    [Widgets.StarsFull]: (m, props) => m.createStarsFullComponent(props),
    [Widgets.StarsFullMark]: (m, props) => m.createStarsFullMarkComponent(props),
    [Widgets.StarsFullReview]: (m, props) => m.createStarsFullReviewComponent(props),
    [Widgets.StarsFullThreeReviews]: (m, props) => m.createStarsFullThreeReviewsComponent(props),
  };

  const creator: (module: WidgetModule, props: WidgetProps<T>) => HTMLElement = creatorMap[widget];

  if (!creator) {
    throw new Error(`Widget type '${widget}' is not supported.`);
  }

  return creator(widgetModule, widgetData);
}

export async function getWidgetData(id: string) {
  try {
    const response = await fetch(siteUrl(`/api/v1/widget?id=${id}`));

    if (!response.ok) {
      throw new Error('Network response was not ok');
    }
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching widget data:', error);
    throw error;
  }
}

export function findMatchingScriptElement(currentUrl: URL): string | null {
  const scriptEl = Array.from(document.scripts).find((script) => {
    try {
      const scriptUrl = new URL(script.src, window.location.origin);
      return scriptUrl.pathname === currentUrl.pathname && scriptUrl.search === currentUrl.search;
    } catch {
      return false;
    }
  });

  if (scriptEl) {
    const scriptUrl = new URL(scriptEl.src);
    const id = scriptUrl.searchParams.get('id');
    if (id) return id;
  }
  return null;
}
